import type { ComponentType } from 'react';
import { Menu, ActionIcon } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import type { Folder } from '../../../schemas/folder.schema';
import {
  getDriveFolderActions,
  type DriveFolderActionComponentProps,
} from '../../registry/defineDriveActions';
import { nearestAnchorRow } from './driveScope';
import {
  canChangeFolderAccess,
  canModifyFolderWithDomain,
  canWriteFolder,
  getEffectiveAccess,
} from './driveAccess';
import { isDomainManagedAnchorCode } from './domainManagePolicy';
import { useFolderArchiveDownload } from './useFolderArchiveDownload';

/**
 * Содержимое модалки доменного действия. `useModal` требует стабильный
 * компонент, а `component` выбирается только в момент клика по пункту меню —
 * поэтому конкретный компонент действия приходит пропсом.
 */
type DriveActionModalContentProps = {
  component: ComponentType<DriveFolderActionComponentProps>;
  folder: Folder;
  scopeAnchor: Folder | null;
  refresh: () => void;
  close: () => void;
};

function DriveActionModalContent({ component: Component, ...props }: DriveActionModalContentProps) {
  return <Component {...props} />;
}

interface FolderActionsMenuProps {
  folder: Folder;
  /** Все папки Диска — для вычисления эффективных прав (корни зон). */
  foldersById: Map<number, Folder>;
  currentUserId: number | null;
  isAdmin: boolean;
  onCreateChild: (parentFolderId: number) => void;
  onRename: (folder: Folder) => void;
  onMove: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onChangeAccess: (folder: Folder) => void;
  /** Перечитать Диск — нужен доменным пунктам из `defineDriveActions`. */
  onRefresh: () => void;
}

/**
 * FolderActionsMenu — меню действий папки (узел дерева, строка таблицы Диска).
 * «Скачать архивом» — всем, кто видит папку (ZIP собирается фоном, сервер
 * проверяет видимость зоны сам); «Новая папка» — по праву записи в зону;
 * управление — по матрице зоны (public_write — автор, public_read/private —
 * владелец зоны, ADMIN — всё); у системных папок управление скрыто.
 * Сервер authoritative.
 *
 * Ниже штатных пунктов — доменные, зарегистрированные модулями через
 * `defineDriveActions` для папок с подходящим якорем. Их доступность платформа
 * не считает: кому можно «Привязать к NCR», знает только сам модуль.
 */
export function FolderActionsMenu({
  folder,
  foldersById,
  currentUserId,
  isAdmin,
  onCreateChild,
  onRename,
  onMove,
  onDelete,
  onChangeAccess,
  onRefresh,
}: FolderActionsMenuProps) {
  const effective = getEffectiveAccess(folder, foldersById);
  // Доменные области учтены (папка инспекции создана системным worker-ом —
  // по «автору объекта» ей не управлял бы никто); сервер авторитетен.
  const canManage = canModifyFolderWithDomain(
    folder,
    foldersById,
    effective,
    currentUserId,
    isAdmin
  );
  // Корень области: rename разрешён, а move/delete запрещены структурными
  // guards (его держит системная раскладка) — кнопки честно гасим, чтобы не
  // показывать действие, которое сервер гарантированно отклонит.
  const isDomainAnchor = folder.code != null && isDomainManagedAnchorCode(folder.code);
  const canRename = canManage;
  const canMove = canManage && !isDomainAnchor;
  const canDelete = canManage && !isDomainAnchor;
  const canCreateInside = canWriteFolder(effective, currentUserId, isAdmin);
  // «Доступ…» — отдельный гейт: в custom-зоне это администрирование (admin+),
  // а у не-корня внутри custom явные режимы запрещены листовостью
  const canAccess = canChangeFolderAccess(folder, effective, currentUserId, isAdmin);
  const { isDownloading, downloadFolderArchive } = useFolderArchiveDownload(folder.id);
  // Модальные доменные действия (component): платформа открывает их через
  // useModal — модулю не нужен свой ModalHost, а z-index из общего аллокатора
  // держит модалку поверх дравера/модалки, из которых открыт Диск.
  const actionModal = useModal(DriveActionModalContent);
  const scopeAnchor = nearestAnchorRow(folder, foldersById);
  // Правила матчатся по ЯКОРЮ области: доменные действия видны и в
  // пользовательских подпапках (у них code = NULL), без запросов из `when`.
  const domainActions = getDriveFolderActions(scopeAnchor ?? folder);

  return (
    <Menu position='bottom-end' withArrow shadow='md'>
      <Menu.Target>
        <ActionIcon
          size='sm'
          variant='subtle'
          color='gray'
          onClick={e => e.stopPropagation()}
          title='Действия'
        >
          <IconDotsVertical size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          disabled={isDownloading}
          onClick={e => {
            e.stopPropagation();
            downloadFolderArchive(folder);
          }}
        >
          {isDownloading ? 'Архив собирается…' : 'Скачать архивом'}
        </Menu.Item>
        <Menu.Item
          disabled={!canCreateInside}
          onClick={e => {
            e.stopPropagation();
            onCreateChild(folder.id);
          }}
        >
          Новая папка
        </Menu.Item>
        {!folder.is_system && (
          <>
            <Menu.Item
              disabled={!canRename}
              onClick={e => {
                e.stopPropagation();
                onRename(folder);
              }}
            >
              Переименовать
            </Menu.Item>
            <Menu.Item
              disabled={!canMove}
              onClick={e => {
                e.stopPropagation();
                onMove(folder);
              }}
            >
              Переместить
            </Menu.Item>
            <Menu.Item
              disabled={!canAccess}
              onClick={e => {
                e.stopPropagation();
                onChangeAccess(folder);
              }}
            >
              Доступ…
            </Menu.Item>
            <Menu.Item
              color='red'
              disabled={!canDelete}
              onClick={e => {
                e.stopPropagation();
                onDelete(folder);
              }}
            >
              Удалить
            </Menu.Item>
          </>
        )}

        {/* Доменные пункты модулей — отдельной группой, чтобы не смешивались
            со штатными операциями над папкой */}
        {domainActions.length > 0 && (
          <>
            <Menu.Divider />
            {domainActions.map(action => (
              <Menu.Item
                key={action.id ?? action.title}
                color={action.color}
                // Изменяющее действие без права записи в зону — гасим (тот же
                // гейт, что у «Новая папка»); доменное право проверяет сервер
                disabled={action.requiresWrite === true && !canCreateInside}
                onClick={e => {
                  e.stopPropagation();
                  if (action.component) {
                    actionModal.open(
                      {
                        component: action.component,
                        folder,
                        scopeAnchor,
                        refresh: onRefresh,
                        close: () => actionModal.close(),
                        title: action.title,
                      },
                      { size: 'md' }
                    );
                    return;
                  }
                  void action.handler?.({ folder, scopeAnchor, refresh: onRefresh });
                }}
              >
                {action.title}
              </Menu.Item>
            ))}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
